import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import LeftSidebar from './components/LeftSidebar';
import RightSidebar from './components/RightSidebar';
import LoginPage from './pages/LoginPage';
// Original components
import HomePage from './components/HomePage';
import TableList from './components/TableList';
import PythonPlayground from './components/PythonPlayground';
import ContactPage from './components/ContactPage';
import MissingImputation from './components/DataQuality/MissingImputation';
import MissingRate from './components/DataQuality/MissingRate';
import ExpertCut from './components/Discretization/ExpertCut';
import IsotonicRegression from './components/Discretization/IsotonicRegression';
import MonotonicLightGBM from './components/Discretization/MonotonicLightGBM';
import MonotonicOptimalBinning from './components/Discretization/MonotonicOptimalBinning';
import QuantileCut from './components/Discretization/QuantileCut';
import IQR from './components/ExtremesValues/IQR';
import CART from './components/RiskDiff/CART';
import PerformanceTesting from './components/RiskDiff/PerformanceTesting/PerformanceTesting';
import Sampling from './components/RiskDiff/Sampling';
import FrequencyModelling from './components/FrequencyModelling/FrequencyModelling';
import SeverityModelling from './components/SeverityModelling/SeverityModelling';
import SeveritySampling from './components/SeverityModelling/SeveritySampling';
import PurePremium from './components/PurePremium/PurePremium';
import LargeClaims from './components/LargeClaims/LargeClaims';
import ZScore from './components/ExtremesValues/ZScore';
import IsolationForest from './components/ExtremesValues/IsolationForest';
import MRPCashflows from './components/MRP/Cashflows/MRPCashflows';
import MRPNumbers from './components/MRP/Numbers/MRPNumbers';
import LRA from './components/RiskQuantification/LRA/LRA';
import MoCC from './components/RiskQuantification/MoCC/MoCC';
import DwtQuantification from './components/RiskQuantification/MoCDownturn/DwtQuantification/DwtQuantification';
import DwtIdentification from './components/RiskQuantification/MoCDownturn/DwtIdentification/DwtIdentification';
import AdditiveModel from './components/CFProjection/AdditiveModel/AdditiveModel';
import MultiplicativeModel from './components/CFProjection/MultiplicativeModel/MultiplicativeModel';
import { SecurityProvider } from './context/SecurityContext';
import ProjectAnalyses from './pages/ProjectAnalyses';
import ProjectDatasets from './pages/ProjectDatasets';
import Enterprises from './pages/Enterprises';
import EnterpriseForm from './pages/EnterpriseForm';
import Users from './pages/Users';
import UserForm from './pages/UserForm';
import CreateProject from './pages/CreateProject';
import AllProjects from './pages/AllProjects';

const theme = createTheme({
  palette: { mode: 'light', primary: { main: '#667eea' }, background: { default: '#f5f7fa' } },
});

function AppContent() {
  const { user } = useAuth();

  return (
    <Box sx={{ display: 'flex' }}>
      <LeftSidebar />
      <Box sx={{ alignSelf: 'flex-start', mt: '260px' }}><RightSidebar /></Box>
      <Box component="main" key={user ? user.id : "guest"} sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3, overflowX: 'auto' }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/data" element={<TableList />} />
          <Route path="/python-playground" element={<PythonPlayground />} />
          <Route path="/contact" element={<ContactPage />} />
                    <Route path="/missing-rate" element={<MissingRate />} />
          <Route path="/missing-imputation" element={<MissingImputation />} />
          <Route path="/expert-cut" element={<ExpertCut />} />
          <Route path="/isotonic-regression" element={<IsotonicRegression />} />
          <Route path="/monotonic-lightgbm" element={<MonotonicLightGBM />} />
          <Route path="/monotonic-binning" element={<MonotonicOptimalBinning />} />
          <Route path="/quantile-cut" element={<QuantileCut />} />
          <Route path="/iqr" element={<IQR />} />
          <Route path="/cart" element={<CART />} />
          <Route path="/performance-testing" element={<PerformanceTesting />} />
          <Route path="/sampling" element={<Sampling />} />
          <Route path="/frequency-modelling" element={<FrequencyModelling />} />
          <Route path="/severity-modelling" element={<SeverityModelling />} />
          <Route path="/severity-sampling" element={<SeveritySampling />} />
          <Route path="/pure-premium" element={<PurePremium />} />
          <Route path="/large-claims" element={<LargeClaims />} />
          <Route path="/zscore" element={<ZScore />} />
          <Route path="/isolation-forest" element={<IsolationForest />} />
          <Route path="/mrp-cashflows" element={<MRPCashflows />} />
          <Route path="/mrp-numbers" element={<MRPNumbers />} />
          <Route path="/additive-projection" element={<AdditiveModel />} />
          <Route path="/multiplicative-projection" element={<MultiplicativeModel />} />
          <Route path="/lra" element={<LRA />} />
          <Route path="/moc-c" element={<MoCC />} />
          <Route path="/dwt-quantification" element={<DwtQuantification />} />
          <Route path="/dwt-identification" element={<DwtIdentification />} />
          <Route path="/enterprises" element={<Enterprises />} />
          <Route path="/enterprises/new" element={<EnterpriseForm />} />
          <Route path="/enterprises/:id/edit" element={<EnterpriseForm />} />
          <Route path="/users" element={<Users />} />
          <Route path="/users/new" element={<UserForm />} />
          <Route path="/users/:id/edit" element={<UserForm />} />
          <Route path="/projects/new" element={<CreateProject />} />
          <Route path="/projects/:id/edit" element={<CreateProject />} />
          <Route path="/projects" element={<AllProjects />} />
          <Route path="/projects/:projectId/analyses" element={<ProjectAnalyses />} />
          <Route path="/projects/:projectId/datasets" element={<ProjectDatasets />} />
        </Routes>
      </Box>
      <Toaster position="top-right" />
    </Box>
  );
}

function AppTwoSidebar() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SecurityProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </SecurityProvider>
    </ThemeProvider>
  );
}

export default AppTwoSidebar;
